import { ControllerHelper, MediaDownloadDto, WikiArticleReviewDownloadDto, WikiColumnDownloadDto, WikiController, WikiSectionDownloadDto } from 'collaboration-service';
import { computeColumnLabel } from './WikiNumberingHelpers';
import CopyToClipboard from 'components/CopyToClipboard/CopyToClipboard';
import { formatRelative } from 'date-fns';
import { AutoGridColumn, Icon, useBelowBreakpoint } from 'imaginarity-react-ui';
import * as _ from "lodash";
import * as React from 'react';
import { Actions } from 'services/ApplicationState/Actions';
import { ApplicationState, useAppDispatch, useAppSelector } from 'services/ApplicationState/ApplicationState';
import { getLink, KeyOfType, searchHtmlAndMark, searchTableAndMark, shallowCompare } from 'services/Helpers';
import { getMediaLink, uploadMediaToStorage } from 'services/Helpers/MediaHelpers';
import { usePrefixedRouteHelpers } from 'services/Helpers/RoutingHelper';
import ScrollHelper from 'services/Helpers/ScrollHelper';
import { getTranslated, getTranslatedExact } from 'services/Helpers/TranslationHelpers';
import { ImgI18N, useImgI18N } from 'services/ImgI18N';
import { useBelowBreakpointOrEqual, useThemePart } from 'services/useAppTheme';
import { setVideoDefaultTrackByLng } from 'views/Tube/TubeContent';
import { UploadFile } from './WikiEditArticle';
import WikiActiveReviewPanel from './WikiActiveReviewPanel';
import WikiColumnContent from './WikiColumnContent';
import WikiColumnReviewFooter from './WikiColumnReviewFooter';
import { WikiSC as T } from './WikiSC';

const elementResizeDetectorMaker = require("element-resize-detector");
const erdUltraFast = elementResizeDetectorMaker({
    strategy: "scroll" //<- For ultra performance.
});

const mapper = (state: ApplicationState) => ({
    search: state.wikiState.search,
    contentLanguage: state.contentLanguage,
    routeParams: state.params,
    config: state.wikiState.config,
    referenceLanguage: state.wikiState.referenceLanguage
});

const EMPTY_STRING_ARRAY: string[] = [];

const getStartTag = (id: string) => {
    return `<b id="#searchResult${id}-{{pos}}" class="searchFinding">` +
        `<b  style="position: absolute; top: -60px; left: 0px" id="#searchResultAnchor${id}-{{pos}}"></b>`;
};

const fromClipboardLink = <T.PulsateIcon>
    <Icon name="chevron right double" style={{ float: "left", marginRight: 5, marginLeft: -3 }} marginTop={5} />
</T.PulsateIcon>;

export type WikiSectionColumnProps = {
    index: number;
    span: number;
    column: WikiColumnDownloadDto;
    reviewMode?: boolean;
    activeColumnIds: string[];
    setActiveColumnIds: React.Dispatch<React.SetStateAction<string[]>>;
    onNewReview?: (review: WikiArticleReviewDownloadDto) => Promise<void>;
    reviews?: WikiArticleReviewDownloadDto[];
    namespace: string;
    numberingEnabled: boolean;
    numberingLevels?: (string[] | string)[];
    parentLabel?: string;
    section: WikiSectionDownloadDto;
    isInEditMode?: boolean;
    addRefs: (name: string, ids?: string[], threshold?: number | number[]) => (r: HTMLDivElement | null) => void;
    scrollHelper?: ScrollHelper;
    availableLngs?: string[];
};

const WikiSectionColumn = React.memo((p: WikiSectionColumnProps) => {
    const { contentLanguage, config, search, routeParams, referenceLanguage } = useAppSelector(mapper, shallowCompare);
    const { column, reviewMode, activeColumnIds, setActiveColumnIds, onNewReview,
        reviews, namespace, numberingEnabled, numberingLevels, parentLabel, span, index, section: s, isInEditMode,
        addRefs, scrollHelper, availableLngs } = p;
    const dispatch = useAppDispatch();
    const [, setLoading] = React.useState<boolean>(false);
    const [showRefLang, setShowRefLang] = React.useState<boolean>(false);
    const [showCurrentReference, setShowCurrentReference] = React.useState<boolean>(true);
    const { createT, t, currentLanguage } = useImgI18N(namespace);
    const colLabel = numberingEnabled ? numberingLevels?.[1] : [];
    const { createNamedRoute } = usePrefixedRouteHelpers("wiki", config.baseRoute);
    const [width, setWidth] = React.useState<number>(0);
    const vidRef = React.useRef<HTMLVideoElement | null>(null);
    const tGeneral = createT("general");

    const rs = vidRef.current?.readyState ?? 0;
    const lng = contentLanguage ?? currentLanguage;

    const articleId = routeParams?.id as string;
    const routeSecId = routeParams?.secId as string;
    const sectionId = s.id;

    const review = React.useMemo(() => {
        const currentLng = contentLanguage ?? ImgI18N.getInstance().currentLanguage;
        const candidates = _.filter(reviews, r => r.partId === column.id && r.reviewLanguage === currentLng);
        const foundReview = _.find(candidates, r => r.id !== undefined) ?? _.first(candidates);
        if (foundReview)
            return foundReview;
        return {
            columnChanges: _.cloneDeep(column),
            partId: column.id,
            articleId: articleId ?? "",
            reviewLanguage: currentLng,
        } as WikiArticleReviewDownloadDto;
    }, [reviews, column, articleId, contentLanguage]);

    const reviewRef = React.useRef(review);
    reviewRef.current = review;
    const onNewReviewRef = React.useRef(onNewReview);
    onNewReviewRef.current = onNewReview;

    React.useEffect(() => {
        if (reviewRef.current.id === undefined) {
            onNewReviewRef.current?.(reviewRef.current);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [column.id, contentLanguage]);

    React.useEffect(() => {
        if (rs === 4)
            setVideoDefaultTrackByLng(vidRef.current, lng);
    }, [vidRef, rs, lng]);

    const windowWith = window.innerWidth;
    const isTablet = useBelowBreakpoint("tablet");
    const scaledWindowWidth = windowWith > 1920 ? 1920 : windowWith;
    const calcedContentWidth = scaledWindowWidth - (isTablet ? 0 : 360);

    const isMobile = useBelowBreakpointOrEqual("mobile");
    const { contentWidth, missingAudioSvg, missingImageSvg, emptyVideoThumbnail, emptyImageUrl, missingVideoSvg, emptyAvatarImageUrl } = useThemePart(s => ({
        contentWidth: s.content.width,
        missingAudioSvg: s.missingAudioSvg,
        missingImageSvg: s.missingImageSvg,
        missingVideoSvg: s.missingVideoSvg,
        emptyVideoThumbnail: s.emptyVideoThumbnail,
        emptyImageUrl: s.emptyImageUrl,
        emptyAvatarImageUrl: s.emptyAvatarImageUrl
    }));

    const { reviewChangedDate, reviewChangedDummyAvatarUrl, reviewChangedUser } = React.useMemo(() => {
        const getDateFnsLng = ImgI18N.getInstance().languageLocal();
        let reviewChangedDate = "Invalid date";
        let reviewChangedUser = review.creator;
        let reviewChangedDummyAvatarUrl = emptyAvatarImageUrl;

        if (review.changed) {
            const changedDate = new Date(review.changed);
            if (!isNaN(changedDate.getTime())) {
                reviewChangedDate = formatRelative(changedDate, new Date(), { locale: getDateFnsLng });
            }
        }

        reviewChangedUser = review.changedBy || review.creator;
        reviewChangedDummyAvatarUrl = getMediaLink(reviewChangedUser, u => u.avatar) ?? emptyAvatarImageUrl;

        return { reviewChangedDate, reviewChangedDummyAvatarUrl, reviewChangedUser };
    }, [review, emptyAvatarImageUrl]);

    const setImageModal = React.useCallback((modal: JSX.Element) => {
        dispatch(Actions.setMasterLayoutExtension(() => modal));
    }, [dispatch]);

    const removeImageModal = React.useCallback(() => {
        dispatch(Actions.setMasterLayoutExtension());
    }, [dispatch]);

    const refLanguageChanged = React.useCallback((lng: string) => {
        dispatch(Actions.setWikiReferenceLanguage(lng));
    }, [dispatch]);

    const closePartReview = React.useCallback(() => {
        setActiveColumnIds(prev => prev.filter(id => id !== column.id));
        setShowRefLang(false);
    }, [setActiveColumnIds, column.id]);

    const openPartReview = React.useCallback(() => {
        setActiveColumnIds(prev => [...prev.filter(id => id !== column.id), column.id]);
    }, [setActiveColumnIds, column.id]);

    const setRef = React.useCallback((ref?: any) => {
        const div = ref as HTMLDivElement;
        if (div) {
            erdUltraFast.listenTo({}, div, (ele: HTMLDivElement) => {
                setWidth(w => Math.floor(ele?.offsetWidth ?? 0));
            });
        }
    }, []);

    const saveReview = React.useCallback(async () => {
        setLoading(true);
        const saved = review.id ?
            await ControllerHelper.singleCall({ id: review.id, dto: review }, WikiController.ChangeReviewArticle) :
            await ControllerHelper.singleCall({ dto: review }, WikiController.CreateReviewArticle);
        await onNewReview?.(saved);
        setLoading(false);
        closePartReview();
    }, [review, onNewReview, closePartReview]);

    const updateReviewColumn = React.useCallback((col: WikiColumnDownloadDto, uploadFile?: UploadFile | UploadFile[]) => {
        const file = (_.isArray(uploadFile) ? uploadFile[0] : uploadFile)?.mediaFile;
        if (file) {
            uploadMediaToStorage(file).then((mediaLink) => {
                if (mediaLink) {
                    col.mediaContent = _.filter((col.mediaContent ?? []), (m: MediaDownloadDto) => m.lng !== review.reviewLanguage);
                    col.mediaContent.push({
                        mediaType: file.type,
                        changed: true,
                        fileName: file.name,
                        size: file.size,
                        links: [],
                        lng: review.reviewLanguage,
                        hasStorageThumbnails: false,
                        container: mediaLink.container,
                        reference: mediaLink.reference,
                    } as any as MediaDownloadDto);
                }
                onNewReview?.({ ...review, columnChanges: col });
            });
        } else {
            onNewReview?.({ ...review, columnChanges: col });
        }
    }, [review, onNewReview]);

    const toggleBoolOfReview = React.useCallback((what: KeyOfType<WikiArticleReviewDownloadDto, boolean>) => () => {
        onNewReview?.({ ...review, [what]: !(review[what] ?? false) });
    }, [review, onNewReview]);

    const colTitle = getTranslated(column.title, contentLanguage).text;
    const colId = column.id;
    const hasColTitle = !!(colTitle && colTitle !== "");

    const titledColsBefore = React.useMemo(() =>
        s.columns.slice(0, index).filter(col => {
            const title = getTranslated(col.title, contentLanguage).text;
            return title && title !== "";
        }).length
        , [s.columns, index, contentLanguage]);

    const label = computeColumnLabel(numberingEnabled, hasColTitle, colLabel, titledColsBefore, parentLabel);

    const media = getTranslated(column.mediaContent, contentLanguage);
    const mediaNotAvailable = !getTranslatedExact(column.mediaContent, contentLanguage) && !!(column.mediaContent?.length) && !!reviewMode;
    const isOverlayImage = column.mediaOverlay.length > 0;
    const overlayData = getTranslatedExact(column.mediaOverlay, contentLanguage);

    let table = getTranslated(column.tableContent, contentLanguage);
    const htmlNotAvailable = !getTranslatedExact(column.textContent, contentLanguage) && !!(column.textContent?.length) && !!reviewMode;
    const tableNotAvailable = !getTranslatedExact(column.tableContent, contentLanguage) && !!(column.tableContent?.length) && !!reviewMode;
    const shortLang = (contentLanguage ?? "").substring(0, 2);
    let text = getTranslated(column.textContent, contentLanguage).text;
    if (search) {
        const starttag = getStartTag(column.id);
        const endtag = "</b>";
        const { html } = searchHtmlAndMark(text, search, starttag, endtag);
        text = html;
        if (column.columnType === "Table") {
            const { tableNew } = searchTableAndMark(table, search, starttag, endtag);
            table = tableNew;
        }
    }

    const imgWidth = media?.width ?? 400;
    const imgHeight = media?.height ?? 280;
    const aspect = imgWidth / imgHeight;
    const uri = getLink(media?.links, "self")?.uri;
    const urlCol = articleId ? window.location.origin + "/#" + createNamedRoute("wiki_article", { id: articleId, secId: colId }) as string : "";
    const isLinkedCol = routeSecId === colId;
    const w = media.mediaType === "image/svg+xml" ? undefined : (Math.min(width, media?.width ?? width));

    const remarkText = getTranslated(review.remark ?? [], review.reviewLanguage).text ?? "";

    const setRemarkText = React.useCallback((text: string) => {
        onNewReview?.({
            ...review,
            remark: [..._.filter(review.remark, r => r.lng !== review.reviewLanguage), { lng: review.reviewLanguage, text }],
        });
    }, [review, onNewReview]);

    const reviewIsSaveable = review.reasonConfirmed
        ? true
        : (review.reasonMarketChanged || review.reasonTranslationChanged)
            ? !(remarkText === "" || remarkText === "<p><br></p>")
            : false;

    const reasonIsSet = !!(review.reasonMarketChanged || review.reasonTranslationChanged || review.reasonConfirmed);
    const isVerified = review.id !== undefined && review.reviewLanguage === contentLanguage;
    const isAutoTranslated = column.textContent?.some(tc => tc.autoTranslation === true && tc.lng === contentLanguage) ?? false;

    const activateRevisionMode = reviewMode;
    const hidden = !reviewMode ? false : (isInEditMode ? (activeColumnIds.includes(column.id) ? false : true) : false);
    if (hidden) {
        return null;
    }

    const isActive = activeColumnIds.includes(column.id);

    return (
        <AutoGridColumn
            span={span}
            key={index}
            zIndex={0}
            divRef={addRefs("column", [sectionId, column.id], 0.1)}
        >
            <T.RevisionContainer
                revisionMode={activateRevisionMode ?? false}
                noHover={isActive}
                reviewed={isVerified}
                style={{ display: hidden ? "none" : "grid" }}
            >
                <div style={{ position: "relative", width: 0, height: 0 }}>
                    <div style={{ position: "absolute", top: -60, left: 0 }} ref={scrollHelper?.getRef(column.id)} />
                </div>
                {!isActive &&
                    <>
                        <T.ColumnTitle
                            isLinkedCol={isLinkedCol}
                            style={{ lineHeight: (colTitle && colTitle !== "" && urlCol) ? "40px" : "1.6rem" }}
                        >
                            {isLinkedCol && fromClipboardLink}
                            {colTitle && colTitle !== "" && label &&
                                <T.ColumnTitleLabel>
                                    {label}
                                </T.ColumnTitleLabel>
                            }
                            {colTitle}
                            {!reviewMode && (colTitle && colTitle !== "" && urlCol) &&
                                <CopyToClipboard
                                    text={urlCol}
                                    type='icon'
                                    copyHint={t("copy link to clipboard")}
                                    copiedHint={t("link copied to clipboard")}
                                />
                            }
                            {isAutoTranslated &&
                                <T.AIHint>
                                    {t("ai translated")}
                                </T.AIHint>
                            }
                        </T.ColumnTitle>

                        <WikiColumnContent
                            column={column}
                            contentLanguage={contentLanguage}
                            htmlNotAvailable={htmlNotAvailable}
                            tableNotAvailable={tableNotAvailable}
                            mediaNotAvailable={mediaNotAvailable}
                            text={text}
                            table={table}
                            media={media}
                            isOverlayImage={isOverlayImage}
                            overlayData={overlayData}
                            aspect={aspect}
                            w={w}
                            width={width}
                            imgWidth={imgWidth}
                            imgHeight={imgHeight}
                            uri={uri}
                            calcedContentWidth={calcedContentWidth}
                            isMobile={isMobile}
                            shortLang={shortLang}
                            contentWidth={contentWidth}
                            columnCount={s.columnCount}
                            t={t}
                            tGeneral={tGeneral}
                            emptyImageUrl={emptyImageUrl}
                            missingImageSvg={missingImageSvg}
                            missingVideoSvg={missingVideoSvg}
                            missingAudioSvg={missingAudioSvg}
                            emptyVideoThumbnail={emptyVideoThumbnail}
                            setImageModal={setImageModal}
                            removeImageModal={removeImageModal}
                            vidRef={vidRef}
                            setRef={setRef}
                        />
                    </>
                }

                {activateRevisionMode &&
                    <WikiColumnReviewFooter
                        isActive={isActive}
                        reviewMode={reviewMode}
                        isVerified={isVerified}
                        reviewChangedDummyAvatarUrl={reviewChangedDummyAvatarUrl}
                        reviewChangedUser={reviewChangedUser}
                        reviewChangedDate={reviewChangedDate}
                        t={t}
                        availableLngs={availableLngs ?? EMPTY_STRING_ARRAY}
                        openPartReview={openPartReview}
                        closePartReview={closePartReview}
                        saveReview={saveReview}
                        reviewIsSaveable={reviewIsSaveable}
                    />
                }
                {reviewMode && isActive &&
                    <WikiActiveReviewPanel
                        column={column}
                        review={review}
                        referenceLanguage={referenceLanguage}
                        contentLanguage={contentLanguage}
                        currentLanguage={currentLanguage}
                        namespace={namespace}
                        refLanguageChanged={refLanguageChanged}
                        availableLngs={availableLngs ?? EMPTY_STRING_ARRAY}
                        updateReviewColumn={updateReviewColumn}
                        toggleBoolOfReview={toggleBoolOfReview}
                        reasonIsSet={reasonIsSet}
                        remarkText={remarkText}
                        setRemarkText={setRemarkText}
                        showRefLang={showRefLang}
                        setShowRefLang={setShowRefLang}
                        showCurrentReference={showCurrentReference}
                        setShowCurrentReference={setShowCurrentReference}
                    />
                }
            </T.RevisionContainer>
        </AutoGridColumn>
    );
});

export default WikiSectionColumn;
